import React from 'react';
import { 
  Shield, 
  Users, 
  Trophy,
  FileText,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Calendar,
  Mail,
  Globe,
  Award,
  Target,
  RefreshCw
} from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black py-20 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <header className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-green-400/10 rounded-lg">
              <FileText className="w-8 h-8 text-green-400" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-white">
              Terms and Conditions
            </h1>
          </div>
          <p className="text-sm text-gray-400 mb-4">
            Last updated: 04/11/2025
          </p>
          <p className="text-lg text-gray-300">
            Welcome to <strong className="text-white">Fokat Main</strong>, operated by{' '}
            <strong className="text-white">PLAYEDGE INNOVATIONS</strong> ("we," "our," or "us"). 
            By accessing or participating in our quizzes, giveaways, or any other activities on{' '}
            <a href="http://www.fokatmain.com" className="text-yellow-400 hover:underline">
              www.fokatmain.com
            </a>, you agree to these Terms and Conditions. 
            Please read them carefully before using our platform.
          </p>
        </header>

        {/* Content Card */}
        <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-gray-800 space-y-8">
          
          {/* Section 1: Nature of Our Platform */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-green-400" />
              <span className="text-yellow-400">1.</span> Nature of Our Platform
            </h2>
            <div className="space-y-3">
              <div className="bg-green-900/20 border border-green-800/50 rounded-lg p-4">
                <p className="text-gray-300">
                  Fokat Main is a <strong className="text-white">skill-based gaming and quiz platform</strong> where 
                  users participate in knowledge-based contests.
                </p>
              </div>
              <div className="bg-green-900/20 border border-green-800/50 rounded-lg p-4">
                <p className="text-gray-300">
                  Winners are rewarded with <strong className="text-white">gadgets, merchandise, or other non-cash prizes</strong>.
                </p>
              </div>
              <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-4">
                <p className="text-gray-300">
                  We <strong className="text-white">do not offer or support gambling, betting, or random chance-based (RNG) activities</strong>.
                </p>
              </div>
              <div className="bg-blue-900/20 border border-blue-800/50 rounded-lg p-4">
                <p className="text-gray-300">
                  All contests are determined solely by the participant's <strong className="text-white">skill, speed, and accuracy</strong>.
                </p>
              </div>
            </div>
          </section>

          {/* Section 2: Eligibility */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-green-400" />
              <span className="text-yellow-400">2.</span> Eligibility
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  Participants must be <strong className="text-white">18 years or older</strong> and residents of India.
                </p>
              </div>
              <div className="flex items-start gap-3 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  Users must provide <strong className="text-white">accurate personal details</strong> during registration.
                </p>
              </div>
              <div className="flex items-start gap-3 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  Fokat Main reserves the right to <strong className="text-white">verify participant identity</strong> before awarding prizes.
                </p>
              </div>
            </div>
          </section>

          {/* Section 3: Participation Rules */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Target className="w-6 h-6 text-green-400" />
              <span className="text-yellow-400">3.</span> Participation Rules
            </h2>
            <div className="space-y-3">
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <p className="text-gray-300">
                  Each quiz or contest is <strong className="text-white">skill-based</strong> and may have specific instructions, 
                  time limits, and participation criteria.
                </p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <p className="text-gray-300">
                  <strong className="text-white">Multiple entries or fraudulent participation</strong> may result in disqualification.
                </p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <p className="text-gray-300">
                  Decisions regarding winners are <strong className="text-white">final</strong> and made based on skill, accuracy, and timing.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4: Quiz Participation Policy */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <FileText className="w-6 h-6 text-green-400" />
              <span className="text-yellow-400">4.</span> Quiz Participation Policy
            </h2>
            <div className="space-y-3">
              <div className="bg-blue-900/20 border border-blue-800/50 rounded-lg p-4">
                <p className="text-gray-300">
                  Each participant may submit <strong className="text-white">one answer per quiz</strong> unless otherwise mentioned.
                </p>
              </div>
              <div className="bg-blue-900/20 border border-blue-800/50 rounded-lg p-4">
                <p className="text-gray-300">
                  Only the <strong className="text-white">first correct answer submitted without any edits</strong> will be considered valid.
                </p>
              </div>
              <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-300">
                    <strong className="text-white">Edited or modified answers will be disqualified</strong>, even if they are corrected later.
                  </p>
                </div>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <p className="text-gray-300">
                  In case multiple participants submit correct answers, the winner will be determined based on the{' '}
                  <strong className="text-white">earliest unedited correct response</strong> received.
                </p>
              </div>
              <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-4">
                <p className="text-gray-300">
                  Any attempt to <strong className="text-white">manipulate submissions or use multiple accounts</strong> for 
                  unfair advantage will lead to disqualification.
                </p>
              </div>
              <div className="bg-yellow-900/20 border border-yellow-800/50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-300">
                    The decision of the Fokat Main team regarding quiz results, timing, and winner selection is{' '}
                    <strong className="text-white">final and binding</strong>.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5: Winner Eligibility and Fair Play */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Award className="w-6 h-6 text-green-400" />
              <span className="text-yellow-400">5.</span> Winner Eligibility and Fair Play
            </h2>
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 border border-purple-800/50 rounded-lg p-5">
                <p className="text-gray-300 mb-3">
                  To ensure equal opportunity, if a participant wins <strong className="text-white">three consecutive quizzes</strong>, 
                  they will be placed on a cooldown and will not be eligible to win the <strong className="text-white">next two quizzes</strong>.
                </p>
                <p className="text-gray-300 mb-3">
                  Such participants may continue to play, but their entries will not be considered for prizes during the cooldown period.
                </p>
                <p className="text-gray-300 mb-3">
                  This policy helps maintain <strong className="text-white">fair competition</strong> and encourages participation from all users.
                </p>
                <p className="text-gray-300">
                  Fokat Main reserves the right to modify this policy to ensure a fair and engaging experience for everyone.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6: Prizes and Giveaways */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Trophy className="w-6 h-6 text-green-400" />
              <span className="text-yellow-400">6.</span> Prizes and Giveaways
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  Winners receive <strong className="text-white">gadgets, gift items, or merchandise</strong> as stated in the contest details.
                </p>
              </div>
              <div className="flex items-start gap-3 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  <strong className="text-white">No cash or monetary equivalent</strong> is offered.
                </p>
              </div>
              <div className="flex items-start gap-3 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  Prizes are <strong className="text-white">non-transferable</strong> and cannot be exchanged for cash.
                </p>
              </div>
              <div className="flex items-start gap-3 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  Delivery timelines depend on logistics and availability; delays may occur due to external factors.
                </p>
              </div>
            </div>
          </section>

          {/* Section 7: Refund and Cancellation */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <RefreshCw className="w-6 h-6 text-green-400" />
              <span className="text-yellow-400">7.</span> Refund and Cancellation
            </h2>
            <div className="bg-gray-800/50 rounded-lg p-5 border border-gray-700">
              <p className="text-gray-300">
                Participation in quizzes or giveaways is <strong className="text-white">final once confirmed</strong>. 
                Refunds are governed by our{' '}
                <a href="/refund-policy" className="text-yellow-400 hover:underline">
                  Refund Policy
                </a>.
              </p>
            </div>
          </section>

          {/* Section 8: Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-green-400" />
              <span className="text-yellow-400">8.</span> Intellectual Property
            </h2>
            <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-5">
              <p className="text-gray-300">
                All content, graphics, logos, and trademarks on the platform belong to{' '}
                <strong className="text-white">PLAYEDGE INNOVATIONS</strong>. 
                Unauthorized use, duplication, or redistribution is <strong className="text-white">strictly prohibited</strong>.
              </p>
            </div>
          </section>

          {/* Section 9: Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-green-400" />
              <span className="text-yellow-400">9.</span> Limitation of Liability
            </h2>
            <p className="text-gray-300 mb-4">
              We strive to provide a fair, smooth, and secure experience, but we are <strong className="text-white">not responsible</strong> for:
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  Technical errors, interruptions, or delays beyond our control.
                </p>
              </div>
              <div className="flex items-start gap-3 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  Delivery delays by third-party logistics providers.
                </p>
              </div>
              <div className="flex items-start gap-3 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  User mistakes, such as incorrect entries or failure to meet quiz conditions.
                </p>
              </div>
            </div>
          </section>

          {/* Section 10: Modifications */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Calendar className="w-6 h-6 text-green-400" />
              <span className="text-yellow-400">10.</span> Modifications
            </h2>
            <div className="bg-gray-800/50 rounded-lg p-5 border border-gray-700">
              <p className="text-gray-300">
                We may update these Terms and Conditions at any time. Updates will be published on this page with a 
                revised "Last Updated" date.
              </p>
            </div>
          </section>

          {/* Section 11: Contact Us */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <Mail className="w-6 h-6 text-green-400" />
              <span className="text-yellow-400">11.</span> Contact Us
            </h2>
            <div className="bg-gradient-to-r from-yellow-900/20 to-orange-900/20 border border-yellow-800/50 rounded-lg p-6">
              <p className="text-gray-300 mb-4">
                For any queries, concerns, or clarifications, contact us at:
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-yellow-400" />
                  <a href="mailto:support@fokatmain.com" className="text-yellow-400 hover:underline font-semibold">
                    support@fokatmain.com
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-yellow-400" />
                  <a href="/contact" className="text-yellow-400 hover:underline font-semibold">
                    https://www.fokatmain.com/contact
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Quick Links */}
          <div className="pt-6 border-t border-gray-800">
            <p className="text-sm text-gray-400 mb-3">Related Pages:</p>
            <div className="flex flex-wrap gap-3">
              <a 
                href="/faq" 
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-white transition-colors duration-200"
              >
                FAQ
              </a>
              <a 
                href="/privacy" 
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-white transition-colors duration-200"
              >
                Privacy Policy
              </a>
              <a 
                href="/refund-policy" 
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-white transition-colors duration-200"
              >
                Refund Policy
              </a>
              <a 
                href="/contact" 
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-white transition-colors duration-200"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms;
